// Rule editor — sentence-builder UI. Persists via PUT /api/firm-config.

import { useEffect, useState } from "react";
import { useAppData } from "../store";
import {
  Btn, IconBtn, TextInput, Toggle,
} from "../components/Primitives";
import { Select } from "../components/Select";
import { MultSlider } from "../components/Multiplier";

interface Props { id: string | "new"; onClose: () => void; }

const FIELDS = [
  "Stage", "Round size", "Sector", "Geography", "Region",
  "Prior exit", "Team size", "Valuation (cap)",
  "Founders", "Founder universities", "Score",
];

const OPS = [
  { value: "is",            label: "is" },
  { value: "is not",        label: "is not" },
  { value: "is one of",     label: "is one of" },
  { value: "is at least",   label: "is at least" },
  { value: "is at most",    label: "is at most" },
  { value: "is in list",    label: "is in (a list)" },
  { value: "contains",      label: "contains" },
  { value: "is unknown",    label: "is unknown" },
];

export function RuleEditModal({ id, onClose }: Props) {
  const { data, saveConfig } = useAppData();
  const rules = data?.config.rules ?? [];
  const isNew = id === "new";
  const existing = !isNew ? rules.find((r) => String(r.id) === id) : undefined;

  const [field, setField] = useState(existing?.field ?? "Stage");
  const [op,    setOp]    = useState(existing?.op ?? "is one of");
  const [val,   setVal]   = useState(
    existing ? (Array.isArray(existing.value) ? (existing.value as any).join(", ") : String(existing.value))
             : "",
  );
  const [mult,  setMult]  = useState(existing?.multiplier ?? 1.0);
  const [hard,  setHard]  = useState(existing?.hard ?? false);
  const [desc,  setDesc]  = useState(existing?.desc ?? "");
  const [label, setLabel] = useState((existing as any)?.label ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const contribution = Math.round(mult * 10);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        id: existing?.id ?? Math.max(0, ...rules.map((r) => r.id)) + 1,
        label: label || desc || `${field} ${op} ${val}`,
        field, op, value: val,
        multiplier: mult, hard, desc,
      };
      const next = isNew
        ? [...rules, payload]
        : rules.map((r) => String(r.id) === id ? { ...r, ...payload } : r);
      await saveConfig({ ...data.config, rules: next });
      onClose();
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!data || isNew) return;
    if (!confirm("Delete this rule?")) return;
    await saveConfig({
      ...data.config,
      rules: rules.filter((r) => String(r.id) !== id),
    });
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 640 }}>
        <div className="modal-head">
          <div className="who">
            <div className="t">{isNew ? "New rule" : "Edit rule"}</div>
            <div className="s">Build the condition, set how much it matters, give it a plain-language name.</div>
          </div>
          <IconBtn ico="x" onClick={onClose} label="Close" />
        </div>
        <div className="modal-body">
          <div>
            <label className="field-label">Condition</label>
            <div style={{
              padding: "14px 16px", background: "var(--bg)",
              border: "1px solid var(--border)", borderRadius: 8,
              display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
              fontSize: 14, lineHeight: 1.7, letterSpacing: "-0.005em",
            }}>
              <span style={{ color: "var(--ink-3)" }}>When</span>
              <Select value={field} onChange={setField} options={FIELDS} style={{ minWidth: 130 }} />
              <Select value={op} onChange={setOp} options={OPS} style={{ minWidth: 140 }} />
              <span className="input" style={{ minWidth: 100, padding: "4px 10px", flex: 1 }}>
                <input
                  value={val} onChange={(e) => setVal(e.target.value)}
                  placeholder="Pre-seed, Seed"
                  style={{ fontSize: 14, fontWeight: 500 }}
                />
              </span>
            </div>
          </div>

          <div>
            <label className="field-label">How much this matters</label>
            <MultSlider value={mult} onChange={setMult} />
          </div>

          <div>
            <label className="field-label">If this condition fails…</label>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <Toggle on={hard} onChange={setHard} />
              <span style={{ fontSize: 13, color: "var(--ink-2)" }}>
                {hard ? "Reject the deal outright" : "Just skip this rule's contribution"}
              </span>
            </div>
          </div>

          <div>
            <label className="field-label">Plain-language name</label>
            <TextInput
              value={label} onChange={setLabel}
              placeholder="e.g. Match firm stage thesis"
            />
          </div>

          <div>
            <label className="field-label">Description (long form, shown in breakdown)</label>
            <TextInput
              value={desc} onChange={setDesc}
              placeholder="e.g. Stage is one of: Pre-seed, Seed"
            />
          </div>

          <div style={{
            borderTop: "1px solid var(--border)", paddingTop: 14,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span className="eyebrow">Preview</span>
            <span style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
              When <strong style={{ color: "var(--ink)", fontWeight: 500 }}>{field}</strong>
              {" "}{op}{" "}
              <strong style={{ color: "var(--ink)", fontWeight: 500 }}>{val || "…"}</strong>,
              add <strong style={{ color: "var(--ink)", fontFamily: "var(--mono)", fontWeight: 600 }}>
              +{contribution}</strong> to the score
              {hard ? "; reject if it fails" : ""}.
            </span>
          </div>
        </div>
        <div className="modal-foot">
          {!isNew && <Btn kind="ghost" size="sm" onClick={remove}>Delete</Btn>}
          <span className="grow" />
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Add rule" : "Save rule"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
